<script setup lang="ts">
import { UIIcon } from '@groover-dev/groover-ui'
import { storeToRefs } from 'pinia'

import InfluencerTextBadge from '~/components/influencer/profile/header/TextBadge.vue'
import VBookmark from '~/components/ui/VBookmark.vue'

import { useCuratorProfilePreviousButton } from '~/composables/useCuratorProfilePreviousButton'

import { useUserStore } from '~/stores/user'

import type { Influencer } from '~/types/influencer'

type Props = {
  influencer: Influencer
  userIsOwner: boolean
  imgSrc: string
  kinds: string
  country?: string
  isContest?: boolean
}

type Emits = {
  editBanner: []
}

const {
  userIsOwner,
  country = undefined,
  isContest = false,
} = defineProps<Props>()
const emit = defineEmits<Emits>()

const { IS_BAND } = storeToRefs(useUserStore())

const { handleChevronClick, hasPreviousRoute } =
  useCuratorProfilePreviousButton()

const shouldShowClientOnlyFallback = computed(
  () => userIsOwner || hasPreviousRoute.value || IS_BAND.value,
)
</script>

<template>
  <div
    class="tw-relative tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-p-6 lg:tw-hidden"
  >
    <!-- ClientOnly used to keep dynamic content out of cdn cache -->
    <ClientOnly>
      <template v-if="shouldShowClientOnlyFallback" #fallback>
        <div class="tw-h-10">
          <!-- div used to maintain spacing -->
        </div>
      </template>
      <button
        v-if="userIsOwner"
        type="button"
        class="tw-mb-5 tw-flex tw-h-5 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center"
        @click="emit('editBanner')"
      >
        <UIIcon
          name="fa-solid:edit"
          class="tw-mr-1 tw-text-white tw-opacity-75"
          size="sm"
        />
        <span class="tw-text-sm tw-font-medium tw-text-white tw-opacity-75">{{
          $t('influencer.profile.slug.profile.nav06')
        }}</span>
      </button>
      <div
        v-else-if="hasPreviousRoute || IS_BAND"
        class="tw-relative tw-mb-5 tw-flex tw-h-5 tw-w-full tw-justify-between"
      >
        <button
          type="button"
          :aria-label="$t('common.goBack')"
          @click="handleChevronClick"
        >
          <UIIcon
            name="fa-solid:chevron-left"
            class="tw-text-white"
            aria-hidden
          />
        </button>
        <VBookmark
          v-if="IS_BAND"
          :influencer="influencer"
          :hide-tooltip-arrow="true"
          class="tw-block md:tw-hidden"
        />
      </div>
    </ClientOnly>
    <div
      class="tw-mb-6 tw-size-40 tw-overflow-hidden tw-rounded-full"
      :class="{
        'tw-rounded-xs tw-shadow-[shadow:0_1px_4px_0_rgba(51,51,51,0.1)]':
          isContest,
      }"
    >
      <img
        :src="imgSrc"
        alt=""
        class="tw-size-full tw-object-cover tw-object-center"
      />
    </div>
    <div
      class="tw-relative tw-mb-2 tw-flex tw-w-screen tw-items-center tw-justify-center"
    >
      <h1
        class="tw-ellipsis tw-text-h5 tw-w-auto tw-max-w-[calc(100%-100px)] tw-text-center tw-text-white"
      >
        {{ influencer.entity }}
      </h1>
      <InfluencerTextBadge :influencer="influencer" />
    </div>
    <div
      class="tw-flex tw-flex-col tw-items-center tw-gap-y-1 tw-text-center tw-font-bold tw-text-white"
    >
      <span class="tw-w-auto tw-capitalize">{{ kinds }}</span>
      <span class="tw-text-sm">{{ country }}</span>
    </div>
  </div>
</template>
